import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircularProgress, Box, Typography } from '@mui/material';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from '@aws-amplify/auth';

const OAuthCallback: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useAuthenticator((context) => [context.user]);
    const [authStatus, setAuthStatus] = useState<string>('Processing OAuth callback...');

    useEffect(() => {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        console.log('OAuth callback received:', { code, state, error });

        if (error) {
            console.error('OAuth error:', error);
            setAuthStatus('Authentication failed');
            setTimeout(() => {
                navigate('/?auth_error=' + error);
            }, 2000);
            return;
        }

        if (code && state) {
            console.log('Processing OAuth callback...');
            setAuthStatus('Exchanging authorization code...');
            
            const processOAuth = async () => {
                try {
                    // Wait a moment for Amplify to process the OAuth callback
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    
                    // Check if we have a valid session
                    const session = await fetchAuthSession();
                    console.log('Session after OAuth:', session);
                    
                    if (session.tokens) {
                        console.log('OAuth successful, user authenticated');
                        setAuthStatus('Authentication successful! Redirecting...');
                        setTimeout(() => {
                            // Clear the OAuth parameters from URL and redirect
                            window.history.replaceState({}, document.title, '/');
                            navigate('/', { replace: true });
                        }, 1500);
                    } else {
                        console.log('No tokens found, waiting for user state update...');
                        setAuthStatus('Finalizing authentication...');
                        
                        // Try again after a short delay
                        setTimeout(async () => {
                            try {
                                const retrySession = await fetchAuthSession();
                                if (retrySession.tokens) {
                                    console.log('OAuth successful on retry');
                                    setAuthStatus('Authentication successful! Redirecting...');
                                    setTimeout(() => {
                                        window.history.replaceState({}, document.title, '/');
                                        navigate('/', { replace: true });
                                    }, 1000);
                                } else {
                                    console.log('Still no tokens, redirecting anyway');
                                    window.history.replaceState({}, document.title, '/');
                                    navigate('/', { replace: true });
                                }
                            } catch (retryError) {
                                console.error('Retry session check failed:', retryError);
                                window.history.replaceState({}, document.title, '/');
                                navigate('/', { replace: true });
                            }
                        }, 2000);
                    }
                } catch (error) {
                    console.error('Error processing OAuth:', error);
                    setAuthStatus('Authentication failed');
                    setTimeout(() => {
                        navigate('/?auth_error=processing_failed');
                    }, 2000);
                }
            };
            
            processOAuth();
            
            // Set a timeout to redirect if authentication takes too long
            const timeout = setTimeout(() => {
                console.log('OAuth callback timeout, redirecting to home');
                setAuthStatus('Authentication timeout');
                navigate('/?auth_error=timeout');
            }, 15000);

            return () => clearTimeout(timeout);
        } else {
            console.log('No valid OAuth parameters, redirecting');
            setAuthStatus('Invalid authentication parameters');
            setTimeout(() => {
                navigate('/');
            }, 2000);
        }
    }, [searchParams, navigate]);

    useEffect(() => {
        if (user) {
            console.log('User authenticated via OAuth, redirecting to home');
            setAuthStatus('Authentication successful! Redirecting...');
            setTimeout(() => {
                navigate('/');
            }, 1000);
        }
    }, [user, navigate]);

    return (
        <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            minHeight="50vh"
            gap={2}
        >
            <CircularProgress size={48} />
            <Typography variant="h6" color="primary">
                {authStatus}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Please wait while we finish signing you in.
            </Typography>
        </Box>
    );
};

export default OAuthCallback;